import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Database, Table, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { exportDatabase } from '../../utils/exportDatabase';

interface TableSchema {
  table_name: string;
  columns: {
    column_name: string;
    data_type: string;
    is_nullable: string;
    column_default: string | null;
  }[];
}

export function DatabaseSchema() {
  const [tables, setTables] = useState<TableSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchSchema();
  }, []);

  const fetchSchema = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_schema_info');

      if (error) throw error;

      setTables(data || []);
    } catch (err) {
      console.error('Error fetching schema:', err);
      setError('Failed to load database schema');
    } finally {
      setLoading(false);
    }
  };

  const toggleTable = (tableName: string) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
    } else {
      newExpanded.add(tableName);
    }
    setExpandedTables(newExpanded);
  };

  const handleExport = async () => {
    const success = await exportDatabase();
    if (success) {
      alert('Database exported successfully!');
    } else {
      alert('Failed to export database. Please check the console for errors.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
          <Database className="h-6 w-6 text-indigo-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">Database Schema</h2>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Database
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {tables.map((table) => (
            <div key={table.table_name} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleTable(table.table_name)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 focus:outline-none"
              >
                <div className="flex items-center">
                  <Table className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="font-medium text-gray-900">{table.table_name}</span>
                  <span className="ml-2 text-sm text-gray-500">
                    ({table.columns.length} columns)
                  </span>
                </div>
                {expandedTables.has(table.table_name) ? (
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                )}
              </button>

              {expandedTables.has(table.table_name) && (
                <div className="border-t">
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Column
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nullable
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Default
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {table.columns.map((column) => (
                          <tr key={column.column_name}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {column.column_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {column.data_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {column.is_nullable === 'YES' ? 'Yes' : 'No'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {column.column_default || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
